/**
 * BrightPath - ExpenseExportModal Component
 * BP-04-04: Expenses Frontend
 */

import React, { useState } from 'react';
import { Download, FileText, FileSpreadsheet, X, Calendar } from 'lucide-react';
import type { ExportFormat, ReportType, ExpenseFilters } from '../types';
import { cn } from '@/core/lib/utils';

interface ExpenseExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (format: ExportFormat, options: ExportOptions) => Promise<void>;
  filters?: ExpenseFilters;
  isExporting?: boolean;
}

interface ExportOptions {
  format: ExportFormat;
  reportType: ReportType;
  dateRange?: { startDate: string; endDate: string };
  includeReceipts?: boolean;
  groupBy?: 'category' | 'child' | 'month';
}

const FORMAT_OPTIONS: { value: ExportFormat; label: string; icon: React.ReactNode; description: string }[] = [
  { value: 'csv', label: 'CSV', icon: <FileText className="w-5 h-5" />, description: 'Spreadsheet compatible' },
  { value: 'pdf', label: 'PDF', icon: <FileText className="w-5 h-5" />, description: 'Printable report' },
  { value: 'excel', label: 'Excel', icon: <FileSpreadsheet className="w-5 h-5" />, description: 'Full formatting' },
];

const REPORT_OPTIONS: { value: ReportType; label: string; description: string }[] = [
  { value: 'summary', label: 'Summary Report', description: 'Overview with totals by category' },
  { value: 'detailed', label: 'Detailed Report', description: 'All expenses with full details' },
  { value: 'tax', label: 'Tax Report', description: 'Tax-deductible expenses for filing' },
  { value: 'insurance', label: 'Insurance Report', description: 'Claims and coverage summary' },
  { value: 'category', label: 'Category Report', description: 'Breakdown by expense category' },
  { value: 'child', label: 'Per-Child Report', description: 'Expenses grouped by child' },
  { value: 'monthly', label: 'Monthly Report', description: 'Month-by-month breakdown' },
];

export function ExpenseExportModal({
  isOpen,
  onClose,
  onExport,
  filters,
  isExporting = false,
}: ExpenseExportModalProps) {
  const [format, setFormat] = useState<ExportFormat>('pdf');
  const [reportType, setReportType] = useState<ReportType>('summary');
  const [dateRange, setDateRange] = useState({
    startDate: filters?.startDate || new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: filters?.endDate || new Date().toISOString().split('T')[0],
  });
  const [includeReceipts, setIncludeReceipts] = useState(false);

  const handleExport = async () => {
    await onExport(format, {
      format,
      reportType,
      dateRange,
      includeReceipts,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-navy/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-warm-gray/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Download className="w-5 h-5 text-sage" />
            <h2 className="text-xl font-semibold text-navy">Export Expenses</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-cream rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-navy mb-3">Export Format</label>
            <div className="grid grid-cols-3 gap-3">
              {FORMAT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setFormat(option.value)}
                  className={cn(
                    'p-4 rounded-xl border-2 text-center transition-all',
                    format === option.value
                      ? 'border-sage bg-sage/5'
                      : 'border-warm-gray/20 hover:border-warm-gray/40'
                  )}
                >
                  <div className={cn(
                    'w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center',
                    format === option.value ? 'bg-sage text-white' : 'bg-cream text-warm-gray'
                  )}>
                    {option.icon}
                  </div>
                  <p className="font-medium text-navy">{option.label}</p>
                  <p className="text-xs text-warm-gray">{option.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Report Type */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value as ReportType)}
              className="w-full px-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none"
            >
              {REPORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} - {option.description}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">Date Range</label>
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full pl-10 pr-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none"
                />
              </div>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full pl-10 pr-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Options */}
          {format === 'pdf' && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={includeReceipts}
                onChange={(e) => setIncludeReceipts(e.target.checked)}
                className="w-4 h-4 rounded text-sage focus:ring-sage"
              />
              <span className="text-navy">Include receipt images (PDF only)</span>
            </label>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-warm-gray/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-navy hover:bg-cream rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="flex items-center gap-2 px-6 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExpenseExportModal;
